// ReportRun.jsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { restApiBase } from "../settings";
import { ProgressBar, Alert } from "react-bootstrap";

// Polling configuration
const POLLING_INTERVALS = {
    NORMAL: 5000,        // 5 seconds for normal polling
    ERROR_RETRY: 2000,   // 2 seconds for transient errors
    MAX_RETRIES: 30,     // Max consecutive errors before giving up (60s total)
};

// Report state categories
const ACTIVE_STATES = ["PENDING", "STARTED", "RUNNING"];  // Continue polling
const FAILURE_STATES = ["FAILED", "TERMINATED", "UNKNOWN"]; // Stop polling, show error

export function ReportRun({ userId, token }) {
    const { reportId } = useParams();
    const [status, setStatus] = useState({
        report_status: { progress: 0, messages: ["Report generation requested"], report_state: "PENDING" },
    });
    const [error, setError] = useState(null);
    const [errorCount, setErrorCount] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;

        const checkStatus = async () => {
            try {
                const res = await fetch(
                    `${restApiBase}report_status?user_id=${userId}&report_id=${reportId}`,
                    { headers: { Authorization: "Bearer " + token } }
                );

                // Handle HTTP errors before parsing response
                if (!res.ok) {
                    const errorText = await res.text();
                    
                    // Auth errors (401, 403) - terminal, redirect to login
                    if (res.status === 401 || res.status === 403) {
                        if (!cancelled) {
                            setError({
                                type: "auth",
                                message: "Authentication failed. Please log in again.",
                                terminal: true
                            });
                        }
                        return;
                    }
                    
                    // Not found (404) - terminal, report doesn't exist
                    if (res.status === 404) {
                        if (!cancelled) {
                            setError({
                                type: "not_found",
                                message: "Report not found. It may have been deleted.",
                                terminal: true
                            });
                        }
                        return;
                    }
                    
                    // Server errors (5xx) - transient, will retry
                    if (res.status >= 500) {
                        throw new Error(`Server error: ${res.status} - ${errorText}`);
                    }
                    
                    // Other client errors (4xx) - terminal
                    throw new Error(`Request failed: ${res.status} - ${errorText}`);
                }

                const data = await res.json();
                if (cancelled) return;

                // Successful response - reset error tracking
                setErrorCount(0);
                setError(null);
                setStatus(data);

                const reportState = data?.report_status?.report_state;

                // Handle report state transitions
                if (reportState === "COMPLETED") {
                    // Success - navigate to detail page
                    navigate("/report/detail/" + reportId);
                } else if (FAILURE_STATES.includes(reportState)) {
                    // Terminal failure - stop polling and show error
                    const errorMessages = {
                        FAILED: "Report generation failed. Please try again or contact support.",
                        TERMINATED: "Report generation was terminated.",
                        UNKNOWN: "Report status is unknown. The report may not exist."
                    };
                    setError({
                        type: "report_failed",
                        message: errorMessages[reportState] || "Report generation encountered an error.",
                        terminal: true,
                        state: reportState
                    });
                } else if (ACTIVE_STATES.includes(reportState)) {
                    // Active processing - continue polling at normal interval
                    setTimeout(checkStatus, POLLING_INTERVALS.NORMAL);
                } else {
                    // Unexpected state - log warning but continue polling
                    console.warn("Unexpected report state:", reportState);
                    setTimeout(checkStatus, POLLING_INTERVALS.NORMAL);
                }

            } catch (err) {
                console.error("Status error:", err);
                
                if (cancelled) return;

                const newErrorCount = errorCount + 1;
                setErrorCount(newErrorCount);

                // Circuit breaker - stop after max retries
                if (newErrorCount >= POLLING_INTERVALS.MAX_RETRIES) {
                    setError({
                        type: "max_retries",
                        message: "Unable to connect to server after multiple attempts. Please refresh the page.",
                        terminal: true
                    });
                    return;
                }

                // Transient error - show warning and retry with shorter interval
                setError({
                    type: "transient",
                    message: `Connection issue (attempt ${newErrorCount}). Retrying...`,
                    terminal: false
                });
                
                setTimeout(checkStatus, POLLING_INTERVALS.ERROR_RETRY);
            }
        };

        checkStatus();

        return () => { cancelled = true; };
    }, [reportId, token, userId, navigate, errorCount]);

    const progress = status.report_status?.progress ?? 0;
    const messages = status.report_status?.messages ?? [];
    const reportState = status.report_status?.report_state;

    return (
        <div className="p-4">
            <h4>Report is running...</h4>
            <div>ID: {reportId}</div>

            {/* Error Alert - shows terminal errors or transient warnings */}
            {error && (
                <Alert 
                    variant={error.terminal ? "danger" : "warning"} 
                    className="mt-3"
                >
                    <Alert.Heading>
                        {error.terminal ? "Error" : "Connection Issue"}
                    </Alert.Heading>
                    <p>{error.message}</p>
                    {/* Action button for auth errors */}
                    {error.terminal && error.type === "auth" && (
                        <button 
                            className="btn btn-primary btn-sm"
                            onClick={() => navigate("/login")}
                        >
                            Go to Login
                        </button>
                    )}
                    {/* Action button for other terminal errors */}
                    {error.terminal && error.type !== "auth" && (
                        <button 
                            className="btn btn-secondary btn-sm"
                            onClick={() => navigate("/")}
                        >
                            Back to Reports
                        </button>
                    )}
                </Alert>
            )}

            {/* Progress UI - only show if no terminal error */}
            {!error?.terminal && (
                <>
                    {/* Current State */}
                    <div className="mb-2">
                        <strong>Status:</strong> {reportState}
                    </div>

                    {/* Progress percentage */}
                    <div className="mb-2" style={{ fontWeight: "bold" }}>
                        Progress: {progress}%
                    </div>

                    {/* Progress bar - animate only for active states */}
                    <ProgressBar 
                        animated={ACTIVE_STATES.includes(reportState)} 
                        now={progress} 
                        variant={error ? "warning" : "primary"}
                    />

                    {/* Status messages from backend */}
                    {messages.length > 0 && (
                        <ul className="mt-2">
                            {messages.map((msg, i) => (
                                <li key={i}>{msg}</li>
                            ))}
                        </ul>
                    )}
                </>
            )}
        </div>
    );
}
